import {PromotionTextTypes} from '~/utils/constants';

import instance from './instance';
import {
  Bids,
  ImagePromotion,
  Locations,
  PromotionPaymentStatus,
  PromotionStatus,
  UrlPromotion,
} from './types';

interface FetchBidsTextPromo {
  promotionType: PromotionTextTypes.Text;
  categoryIds: number[];
  geo: {
    cityIds: number[];
    countyIds: number[];
    stateIds: number[];
    countryIds: number[];
  };
}

interface FetchBidsImagePromo {
  promotionType: PromotionTextTypes.Image;
  categoryIds: number[];
  geo: {
    cityIds: number[];
    countyIds: number[];
    stateIds: number[];
    countryIds: number[];
  };
}

interface FetchBidsUrlPromo {
  promotionType: PromotionTextTypes.Url;
  categoryIds: number[];
  geo: {
    cityIds: number[];
  };
}

export type FetchBidsParams = FetchBidsTextPromo | FetchBidsImagePromo | FetchBidsUrlPromo;

export interface FetchBidsResult {
  bid_recommended_price: number;
}

interface FetchRecommendedBidsBody {
  promo: PromotionTextTypes;
  category_ids?: number[];
  geo?: {
    city_ids: number[];
    county_ids: number[];
    state_ids: number[];
    country_ids: number[];
  };
}

export const fetchRecommendedBids = (params: FetchBidsParams) => {
  const body: FetchRecommendedBidsBody = {
    promo: params.promotionType,
  };

  if (params.promotionType === PromotionTextTypes.Image) {
    body.category_ids = params.categoryIds;
    body.geo = {
      city_ids: params.geo.cityIds,
      county_ids: params.geo.countyIds,
      state_ids: params.geo.stateIds,
      country_ids: params.geo.countryIds,
    };
  }

  if (params.promotionType === PromotionTextTypes.Text) {
    body.category_ids = params.categoryIds;
    body.geo = {
      city_ids: params.geo.cityIds,
      county_ids: params.geo.countyIds,
      state_ids: params.geo.stateIds,
      country_ids: params.geo.countryIds,
    };
  }

  if (params.promotionType === PromotionTextTypes.Url) {
    body.category_ids = params.categoryIds;
    body.geo = {
      city_ids: params.geo.cityIds,
      county_ids: [],
      state_ids: [],
      country_ids: [],
    };
  }

  return instance.post<FetchBidsResult>('/frontend/api/v1/ad-bids-recommended', body);
};

export const CHECK_URL_RESPONSES_MAP = {
  ERROR_TYPE_EMAIL: {
    title: "Oops! It looks like you've entered an email address.",
    content:
      "Please enter the URL to your profile on another website, like 'http://www.example.com/yourusername'.",
  },
  ERROR_TYPE_IP: {
    title: "Oops! It appears you've entered an IP address.",
    content:
      "We need the URL to your profile on another website. A valid URL looks like this: 'http://www.example.com/yourusername'.",
  },
  ERROR_TYPE_WRONG_FORMAT: {
    title: "Hmm, something's not quite right.",
    content:
      "We couldn't recognize the URL you've entered. A proper URL usually begins with 'http://' or 'https://' and includes a domain name like 'www.example.com/yourusername'. Please check and try again.",
  },
  ERROR_TYPE_BLACKLISTED: {
    title: "We're sorry, but we can't accept the URL you've entered.",
    content:
      "Please ensure you're entering the URL of your personal profile on a supported website, for instance 'http://www.goodwebsite.com/yourusername'.",
  },
  ERROR_TYPE_WRONG_REGEXP: {
    title: "We noticed that you've entered a URL for a website's front page.",
    content:
      'We need the direct public URL of your personal profile on that site. Please check and try again.',
  },
  ERROR_TYPE_ACCESS: {
    title: 'URL you provided is not accessible.',
    content: '',
  },
};

export type CheckUrlResponseStatus = keyof typeof CHECK_URL_RESPONSES_MAP;

export const checkUrl = (url: string) => instance.get<any>(`/frontend/api/v1/check-url?url=${url}`);

/**
 * @deprecated Use createCommonPromotion instead
 */
export const createPromotion = (promotion: any) =>
  instance.post<any>(`/frontend/api/v1/promotions`, {
    promo: promotion.type,
    details: {
      title: promotion.details.headline,
      description: promotion.details.description,
      url: promotion.details.url,
      display_url: promotion.details.displayUrl,
    },
    bids: {
      price: parseFloat(promotion.bids.cpc),
      daily_limit: parseFloat(promotion.bids.daily),
    },
    category_ids: promotion.categories,
    geo: {
      city_ids: promotion.locations,
      county_ids: [],
      state_ids: [],
      country_ids: [],
    },
  });

interface CreatePromotionResponse {
  promo_id: number;
  status: PromotionStatus;
  amount: number;
  payment_status: PromotionPaymentStatus;
  rest_balance: number;
}

export const createCommonPromotion = (payload: any) =>
  instance.post<CreatePromotionResponse>(`/frontend/api/v1/promotions`, payload);

export enum ImagesPromoVariants {
  EscortsModel = 'escorts_model',
  EscortsPlace = 'escorts_place',
  EscortsUrl = 'escorts_url',
  EscortsOnlyfans = 'escorts_onlyfans',
  BusinessModel = 'business_model',
  BusinessPlace = 'business_place',
  BusinessUrl = 'business_url',
}

export interface TextPromotionPayload {
  promo: PromotionTextTypes.Text;
  details: {
    title: string;
    description: string;
    url: string;
    display_url: string;
  };
  bids: {
    price: number;
    daily_limit: number;
  };
  category_ids: number[];
  geo: {
    city_ids: number[];
    county_ids: [];
    state_ids: [];
    country_ids: [];
  };
}

interface ImagePromotionEscortsPayload {
  promo: PromotionTextTypes.Image;
  geo: {
    city_ids: number[];
    county_ids: [];
    state_ids: [];
    country_ids: [];
  };
  details: {
    type:
      | ImagesPromoVariants.EscortsModel
      | ImagesPromoVariants.BusinessUrl
      | ImagesPromoVariants.EscortsOnlyfans;
    // 1 - Поиск по номеру телефона
    // -- Если нашли модель по номеру телефона
    model_id?: string; // Например: js:1483711:7653492291 - приходит в ответе get-profile-by-contact
    thumb_id?: string; // id - конкретного изображения - приходит в ответе get-profile-by-contact
    // -- Если Не нашли модель
    website_url?: string;
    images?: string[]; // Если пользователь Адвертайзер и websiteUrl не эскортный
    auto_renew: boolean;
  };
  // 1 - Поиск по onlyfans
  // Поиск по onlyfans - также передам modelId из get-profile-by-contact
  category_ids?: number[];
  // model_id?: string; // Например: onlyfans:onlyfans - приходит в ответе get-profile-by-contact
  // images?: string[];
}

interface ImagePromotionBusinessPayload {
  promo: PromotionTextTypes.Image;
  bids: {
    price: number;
    daily_limit: number;
  };
  details: {
    type:
      | ImagesPromoVariants.EscortsPlace
      | ImagesPromoVariants.BusinessPlace
      | ImagesPromoVariants.BusinessModel
      | ImagesPromoVariants.BusinessUrl;
    // -- Если нашли модель по номеру телефона
    model_id?: string; // Например: js:1483711:7653492291 - приходит в ответе get-profile-by-contact
    thumb_id?: string; // id - конкретного изображения - приходит в ответе get-profile-by-contact
    // -- Если Не нашли модель
    website_url?: string;
    images?: string[]; // Если пользователь Адвертайзер и websiteUrl не эскортный
  };
}

export type ImagePromotionPayload = ImagePromotionEscortsPayload | ImagePromotionBusinessPayload;

export enum UrlPromotionsType {
  Url = 'url',
  Onlyfans = 'onlyfans',
}

interface UrlPromotionOnlyfansPayload {
  promo: PromotionTextTypes.Url;
  geo: {
    city_ids: number[];
    county_ids: [];
    state_ids: [];
    country_ids: [];
  };
  category_ids: number[];
  bids: {
    price: number;
    daily_limit: number;
  };
  details: {
    type: UrlPromotionsType.Onlyfans;
    url: string;
    onlyfans: string;
    headline: string;
    description: string;
    site_id?: number;
    domain_id?: number;
    page_id?: number;
  };
}

interface UrlPromotionWebsitePayload {
  promo: PromotionTextTypes.Url;
  geo: {
    city_ids: number[];
    county_ids: [];
    state_ids: [];
    country_ids: [];
  };
  category_ids: number[];
  bids: {
    price: number;
    daily_limit: number;
  };
  details: {
    type: UrlPromotionsType.Url;
    url: string;
    site_id?: number;
    domain_id?: number;
    page_id?: number;
    headline: string;
    description: string;
  };
}

export type UrlPromotionPayload = UrlPromotionOnlyfansPayload | UrlPromotionWebsitePayload;

export interface TLocationPrice {
  price: number;
  discountPrice: number;
  discountPercent: number;
}

export const fetchLoactionPrices = () =>
  instance.get<TLocationPrice>('frontend/api/v1/promotions/price');

type FetchPromotionByIdResponse = UrlPromotion | ImagePromotion;

export const fetchPromotionById = (id: string | number) =>
  instance.get<FetchPromotionByIdResponse>(`/frontend/api/v1/promotions/${id}`);

export interface UrlPromotionBody {
  id: number;
  details: {
    name: string;
    url: string;
    title: string;
    description: string;
  };
  bids: {
    price: number;
    daily_limit: number;
  };
  geo: Locations;
  category_ids: number[];
}

export const updateUrlPromotion = (data: UrlPromotionBody) =>
  instance.post(`/frontend/api/v1/promotions/update_url_promo`, data);

export interface UpdateImagePromotionBody {
  id: number;
  category_ids: number[];
  geo: Locations;
  bids?: Bids;
  details: {
    name: string;
    thumb: string[];
    website_url: string;
    images: {
      model_id: string;
      images: [
        {
          id: string;
          src: string;
          status: string;
        },
      ];
    }[];
    site_id: number;
    auto_renew?: boolean;
  };
}

export const updateImagePromotion = (data: UpdateImagePromotionBody) =>
  instance.post(`/frontend/api/v1/promotions/update-image-promo`, data);
